import React from 'react';
import Badge from './Badge';
import { BadgeColor } from './types/badgeTypes';

export interface SubTab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
  color?: BadgeColor;
  /** Optional group key — when adjacent tabs have different groups a divider is shown */
  group?: string;
}

interface SubTabBarProps {
  tabs: SubTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Action buttons rendered on the right side of the tab bar */
  actions?: React.ReactNode;
  /** Additional CSS classes for the outer wrapper (e.g. 'px-4') */
  className?: string;
}

/**
 * Shared sub-tab navigation bar used across matter detail tabs
 * (Sources, Q&As, Notes, Tasks, Drafts, Insights).
 *
 * Renders a horizontal row of tab buttons with optional icons, badge counts,
 * group dividers, and right-aligned action buttons.
 */
const SubTabBar: React.FC<SubTabBarProps> = ({ tabs, activeTab, onTabChange, actions, className }) => {
  return (
    <div className={`border-b border-gray-200 dark:border-gray-700 flex justify-between items-center ${className || ''}`}>
      <nav className="flex items-center space-x-1">
        {tabs.map((tab, index) => {
          const prevGroup = index > 0 ? tabs[index - 1].group : null;
          const showDivider = prevGroup && tab.group && tab.group !== prevGroup;

          return (
            <React.Fragment key={tab.id}>
              {showDivider && (
                <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-1.5" />
              )}
              <button
                onClick={() => onTabChange(tab.id)}
                className={`flex items-center px-4 pt-2 pb-3 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-300'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                }`}
              >
                {tab.icon && <span className="mr-2">{tab.icon}</span>}
                <span>{tab.label}</span>
                {tab.count !== undefined && tab.count > 0 && (
                  <Badge color={tab.color || 'gray'} className="ml-2">
                    {tab.count}
                  </Badge>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>
      {actions && (
        <div className="flex items-center gap-2 pr-4">
          {actions}
        </div>
      )}
    </div>
  );
};

export default SubTabBar;
